import { useActionState, useEffect, useState, startTransition } from 'react'
import { useRouter } from 'next/navigation'
import {
  authenticateWithPasswordAction,
  verifyEmailOTPAction,
  resendEmailVerificationCode,
} from '@/lib/services/auth/login.service'
import { useToastNotification } from '@/hooks/useToastNotification'
import { analytics } from '@/lib/services/analytics.service'
import { logger } from '@/lib/services/logger.service'
import type { LoginState } from '@/types'
import { VALIDATION_RULES } from '@/lib/constants'

/**
 * Custom hook for managing the two-step login flow state and side effects.
 *
 * Orchestrates the entire login flow:
 *
 * 1. Email + password authentication (first factor)
 * 2. Email OTP verification (second factor)
 * 3. Success redirect
 *
 * Also manages:
 *
 * - Resend code functionality with cooldown
 * - Toast notifications
 * - Step transitions
 *
 * @returns Object containing all state, actions, and helpers for the login flow
 */
export function useLoginFlow(returnUrl?: string) {
  const router = useRouter()

  // Server action state for password authentication (step 1)
  const [passwordState, passwordAction, passwordPending] = useActionState<
    LoginState,
    FormData
  >(authenticateWithPasswordAction, { step: 'password' })

  // Server action state for OTP verification (step 2)
  const [otpState, otpAction, otpPending] = useActionState<
    LoginState,
    FormData
  >(verifyEmailOTPAction, { step: 'email-verification' })

  // Server action state for resending OTP code
  const [resendState, resendAction, resendPending] = useActionState<
    LoginState,
    FormData
  >(resendEmailVerificationCode, { step: 'email-verification' })

  // Local state for resend cooldown timer (prevents spam)
  const [resendCooldown, setResendCooldown] = useState(0)

  const isSuccess =
    (passwordState?.success && passwordState?.step === 'success') ||
    (otpState?.success && otpState?.step === 'success')

  // otpState outlives the attempt that produced it: a failure that resets to
  // the password step (e.g. the session cookie is too large) consumed the
  // single-use code, and a later password success must open a fresh OTP form
  // without that old error. Pair each otpState with the passwordState it ran
  // under; it only counts while that passwordState is still current.
  const [otpAttempt, setOtpAttempt] = useState({
    otp: otpState,
    password: passwordState,
  })
  if (otpAttempt.otp !== otpState) {
    setOtpAttempt({ otp: otpState, password: passwordState })
  }
  const otpIsCurrent = otpAttempt.password === passwordState
  const otpError = otpIsCurrent ? otpState?.error : undefined
  const otpRestarted = otpIsCurrent && otpState?.step === 'password'
  const passwordError = otpRestarted ? otpState.error : passwordState?.error

  // Determine which step of the login flow to show. Success is checked first:
  // passwordState keeps its 'email-verification' step after the OTP succeeds, so
  // testing it first would leave the form live and let the user resubmit a
  // single-use code that is already consumed.
  const currentStep = isSuccess
    ? 'success'
    : passwordState?.step === 'email-verification' && !otpRestarted
      ? 'email-verification'
      : 'password'

  // Handle successful authentication by redirecting to the appropriate page
  useEffect(() => {
    if (isSuccess) {
      analytics.track('login.success')
      logger.info('Login flow succeeded', {
        flow: 'login',
        viaOtp: otpState?.success === true,
      })
      // Navigate to return URL (if provided and safe) or home page
      router.push(returnUrl || '/')
    }
  }, [isSuccess, otpState?.success, returnUrl, router])

  // Track password-step failures
  useEffect(() => {
    if (passwordState?.error) {
      analytics.track('login.password_failed', {
        errorCode: passwordState.errorCode,
      })
      logger.warn('Login password step failed', {
        flow: 'login',
        errorCode: passwordState.errorCode,
      })
    }
  }, [passwordState?.error, passwordState?.errorCode])

  // Track OTP-step failures
  useEffect(() => {
    if (otpState?.error) {
      analytics.track('login.otp_failed', {
        errorCode: otpState.errorCode,
      })
      logger.warn('Login OTP step failed', {
        flow: 'login',
        errorCode: otpState.errorCode,
      })
    }
  }, [otpState?.error, otpState?.errorCode])

  // Handle resend cooldown timer countdown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Start cooldown when an OTP code is sent (either from password step or resend)
  // Wrapped in startTransition to mark as non-urgent update and avoid cascading render warning
  useEffect(() => {
    const initialCodeSent =
      passwordState?.success && passwordState?.step === 'email-verification'
    const resendCodeSent = resendState?.success

    if (initialCodeSent || resendCodeSent) {
      startTransition(() => {
        setResendCooldown(VALIDATION_RULES.RESEND_COOLDOWN_SECONDS)
      })
    }
  }, [passwordState?.success, passwordState?.step, resendState])

  // Automatically show toast notifications for password and resend actions
  useToastNotification(passwordState?.toast)
  useToastNotification(resendState?.toast)

  /**
   * Handle resending the verification code. Creates FormData with the user's
   * email and pending auth token, then dispatches resend action.
   */
  const handleResend = () => {
    if (
      !passwordState?.email ||
      !passwordState?.pendingAuthToken ||
      resendCooldown > 0
    )
      return

    const formData = new FormData()
    formData.append('email', passwordState.email)
    formData.append('pendingAuthToken', passwordState.pendingAuthToken)
    startTransition(() => resendAction(formData))
  }

  return {
    // Current step in the flow
    currentStep,

    // Errors for the form on screen, scoped to the current login attempt
    passwordError,
    otpError,

    // State from server actions
    passwordState,
    otpState,
    resendState,

    // Action dispatchers
    passwordAction,
    otpAction,
    resendAction,

    // Pending states
    passwordPending,
    otpPending,
    resendPending,

    // Resend helpers
    handleResend,
    isResendDisabled: resendPending || resendCooldown > 0,
    resendCooldown,
  }
}
